import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { Typography, Anchor, Badge } from '@ui/components';
import { useMutation } from '@tanstack/react-query';
import { OnboardingStep } from '@interfaces/models';
import { setOnboardingStep } from '@api/onboarding';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, FC } from 'react';
import { Routes } from '@shared/enums';

const REDIRECT_TIMEOUT = 5000;

export const OnboardingTopUpBalanceSuccess: FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { hasSubscriptionsAssigned } = useOutletContext<{ hasSubscriptionsAssigned: boolean }>();

  const redirectUrl = useMemo(
    () => `../${hasSubscriptionsAssigned ? Routes.COMPANY_POLICY : Routes.SUBSCRIPTIONS}`,
    [hasSubscriptionsAssigned],
  );

  const { mutate: setStep } = useMutation({
    mutationFn: () =>
      setOnboardingStep(
        hasSubscriptionsAssigned ? OnboardingStep.COMPANY_POLICY : OnboardingStep.SUBSCRIPTIONS,
      ),
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      navigation(redirectUrl, { replace: true });
    }, REDIRECT_TIMEOUT);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [navigation, redirectUrl]);

  useEffect(() => {
    setStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-8">
      <Typography variant="h400" element="h1">
        {t('ONBOARDING.TOP_UP.TITLE')}
      </Typography>

      <div className="flex flex-col items-center justify-center gap-7 p-12 border border-border-weak-hover rounded-xl">
        <Typography variant="h300" element="h3">
          {t('ONBOARDING.TOP_UP.PAYMENT_SUCCESS_TITLE')}
        </Typography>
        <div className="flex items-center gap-2">
          <Typography variant="labelLargeBook" element="span">
            {t('ONBOARDING.TOP_UP.PAYMENT_STATUS')}
          </Typography>
          <Badge variant="green" size="large">
            {t('ONBOARDING.TOP_UP.CONFIRMED_STATUS')}
          </Badge>
        </div>
      </div>

      <div className="flex flex-col items-start gap-2">
        <Typography variant="paragraphLargeBook">
          {t('ONBOARDING.TOP_UP.REDIRECT_MESSAGE')}
        </Typography>
        <Anchor>
          <Link to={redirectUrl} replace>
            <Typography variant="labelMediumMedium" className="text-surface">
              {t('ONBOARDING.TOP_UP.REDIRECT_ME_NOW')}
            </Typography>
          </Link>
        </Anchor>
      </div>
    </div>
  );
};
